import axios, {
  AxiosError,
  isAxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import type Alert from "@/interfaces/Alerts/Alert";
import type ConfigurationFileContents from "@/interfaces/Configuration/ConfigurationFileContents";
import type Notification from "@/interfaces/Notifications/Notification";
import type WebLoginAuthenticationTokens from "@/interfaces/WebLogin/WebLoginAuthenticationTokens";
import { AlertType } from "@/enumerations/Alerts/AlertType";
import { NotificationType } from "@/enumerations/Notifications/NotificationType";
import { getNewerKeptSessionTokens, isKeptSessionOf, isSameWebLogin } from "@/helpers/WebLogin/WebLoginHelpers";
import router from "@/router";
import { useAlertsStore } from "@/stores/alerts";
import { useAuthStore } from "@/stores/auth";
import { useNotificationsStore } from "@/stores/notifications";
import {
  ActivityCategory,
  PortalActivityRefusedError,
  toEndpointTemplate,
  trackActivity,
  type ActivityChannel,
  type PortalActivityBatch,
} from "@/services/activity/portalActivityTracker";
import { getActivitySessionId, getSessionDeviceClass } from "@/services/activity/portalActivitySession";

const REFRESH_TOKEN_ENDPOINT = "api/customerPortal/v1/webLogin/refreshToken";
const ACTIVITY_SEND_TIMEOUT = 10000;
const ACTIVITY_ENDPOINTS: Record<ActivityChannel, string> = {
  account: "api/customerPortal/v1/activity",
  session: "api/customerPortal/v1/activity/session",
  anonymous: "api/customerPortal/v1/activity/anonymous",
};
const REFRESH_TIMEOUT = 30000;
// how long a tab whose refresh lost to another tab's waits for that tab to store the new tokens, where tabs cannot take turns
const UNLOCKED_REFRESH_RACE_WAIT = 2000;
// where tabs cannot take turns, each kept tab marks in local storage when its refresh is out, so a tab whose refresh
// is refused waits only when another tab's refresh may have won
const REFRESH_MARK_PREFIX = "tokenRefreshOut:";
const TAB_REFRESH_MARK = REFRESH_MARK_PREFIX + Math.random().toString(36).slice(2);

type PortalRequestConfig = InternalAxiosRequestConfig & {
  retriedAfterTokenRefresh?: boolean;
  // refused 401 and the token could not be renewed for an outage, so never judged
  renewalFailed?: boolean;
};

let runtimeConfiguration: ConfigurationFileContents | undefined;
let refreshRequest: Promise<RefreshOutcome | undefined> | undefined;
let renewalFailureRecorded: Promise<RefreshOutcome | undefined> | undefined;

export const portalHttpClient = axios.create();

//A caller that reports the failure itself says so here, so the grower is told once and in the caller's own
//words rather than twice, the second time as the status line axios produces.
type AlertSuppressingRequestConfig = { suppressErrorAlert?: boolean };

//Typed as a request config so it can be passed on its own, not only spread into one that carries params.
export const suppressErrorAlert: AxiosRequestConfig & AlertSuppressingRequestConfig =
  { suppressErrorAlert: true };

// Axios 1.15.0+ rejects header values containing CR or LF and aborts the
// request client-side, so strip them from values sourced from config/account data.
function sanitizeHeaderValue(value: string): string {
  return value.replace(/[\r\n]/g, "");
}

function applyRuntimeConfiguration(requestConfig: AxiosRequestConfig = {}) {
  const authStore = useAuthStore();

  requestConfig.params = requestConfig.params ?? {};
  requestConfig.headers = requestConfig.headers ?? {};

  if (runtimeConfiguration?.publicApiKey) {
    requestConfig.params["api-key"] = runtimeConfiguration.publicApiKey;
  }

  if (runtimeConfiguration?.customerPortalKey) {
    requestConfig.headers["Customer-Portal-Key"] = sanitizeHeaderValue(
      runtimeConfiguration.customerPortalKey
    );
  }

  if (requestConfig.headers["Customer-Id"] === undefined) {
    requestConfig.headers["Customer-Id"] = sanitizeHeaderValue(
      authStore.selectedAccount?.id ?? ""
    );
  }

  // kept when given, as Customer-Id is, so a request sent again still names the account it was sent for, and held
  // usage events go under the account and session they were recorded in
  if (requestConfig.headers["Entity-Id"] === undefined) {
    requestConfig.headers["Entity-Id"] = sanitizeHeaderValue(
      authStore.selectedAccount?.entityId ?? ""
    );
  }

  requestConfig.headers["Portal-Session"] ??= getActivitySessionId();
  requestConfig.headers["Portal-Device"] ??= getSessionDeviceClass();

  if (authStore.tokens?.jsonWebToken) {
    requestConfig.headers.Authorization =
      requestConfig.headers.Authorization ??
      `Bearer ${authStore.tokens.jsonWebToken}`;
  }

  return requestConfig;
}

function buildRefreshRequestConfiguration(
  tokens: WebLoginAuthenticationTokens
): AxiosRequestConfig {
  const refreshRequestConfig = applyRuntimeConfiguration({
    method: "post",
    url: REFRESH_TOKEN_ENDPOINT,
    // The refresh request is sent through the bare axios instance (to bypass
    // the 401 interceptor), which does not inherit portalHttpClient's baseURL —
    // without it the request would hit the portal origin instead of the API.
    baseURL: portalHttpClient.defaults.baseURL,
    // the API refuses a portal request with no Authorization header, so send the token being refreshed
    headers: { Authorization: `Bearer ${tokens.jsonWebToken}` },
    // a refresh holds every tab's next refresh behind it, so it must not wait for ever
    timeout: REFRESH_TIMEOUT,
    data: {
      jsonWebToken: tokens.jsonWebToken,
      refreshToken: tokens.refreshToken,
    },
  });

  return refreshRequestConfig;
}

const SESSION_EXPIRED_MESSAGE = "Session ended due to inactivity";
const SESSION_ENDED_MESSAGE = "Your session has ended. Please sign in again.";
export const SESSION_CHANGED_IN_ANOTHER_TAB_MESSAGE = "You signed in or out in another tab.";
// the Public API's answer to a current token asking for an account its login is not mapped to
const ACCOUNT_NOT_HELD_REPLY = "Invalid customer ID";
const ACCOUNT_NOT_AVAILABLE_MESSAGE = "That account is no longer available to you";

async function redirectToLoginWithSessionExpiredAlert(headerMessage = SESSION_EXPIRED_MESSAGE) {
  const authStore = useAuthStore();
  const alertsStore = useAlertsStore();

  authStore.clearState();
  alertsStore.setAlert({
    headerMessage,
    bodyMessage: "",
    type: AlertType.Information,
  } as Alert);

  if (router.currentRoute.value.name !== "login") {
    await router.push("/login");
  }
}

// issuedByApi: the API has just issued these tokens, rather than this tab taking another tab's, which may have expired too
interface RefreshOutcome {
  tokens: WebLoginAuthenticationTokens;
  issuedByApi: boolean;
}

function setRefreshMark(out: boolean) {
  try {
    if (out) {
      localStorage.setItem(TAB_REFRESH_MARK, String(Date.now()));
    } else {
      localStorage.removeItem(TAB_REFRESH_MARK);
    }
  } catch {
    // storage refused: without the mark a refused refresh only stops waiting for another tab
  }
}

// A mark older than a refresh can take was left by a tab closed while its refresh was out, and is removed.
function isAnotherTabRefreshing() {
  try {
    const otherMarks = Object.keys(localStorage).filter(
      (key) => key.startsWith(REFRESH_MARK_PREFIX) && key !== TAB_REFRESH_MARK
    );
    let refreshing = false;
    for (const key of otherMarks) {
      // a mark dated ahead of now was written before the clock went back, so it says nothing about now
      const age = Date.now() - Number(localStorage.getItem(key));
      if (age >= 0 && age < REFRESH_TIMEOUT) {
        refreshing = true;
      } else {
        localStorage.removeItem(key);
      }
    }
    return refreshing;
  } catch {
    // storage refused: no other tab's refresh can be seen
    return false;
  }
}

async function getNewerKeptSessionTokensWithin(tokens: WebLoginAuthenticationTokens, milliseconds: number) {
  const giveUpAt = Date.now() + milliseconds;
  for (;;) {
    const newerTokens = getNewerKeptSessionTokens(tokens);
    if (newerTokens || Date.now() >= giveUpAt) {
      return newerTokens;
    }

    await new Promise((resolve) => setTimeout(resolve, 100));
  }
}

async function refreshAuthenticationTokens(): Promise<RefreshOutcome | undefined> {
  const authStore = useAuthStore();
  // the tokens the refusal was for; the lock can be granted after this tab has taken up newer ones
  const refused = authStore.tokens;

  const refresh = async (underLock: boolean): Promise<RefreshOutcome | undefined> => {
    const tokens = authStore.tokens;
    if (!tokens) {
      return undefined;
    }

    const sharesSession = authStore.webLogin?.keepLoggedIn === true;
    const useTokensFrom = (refreshedTokens: WebLoginAuthenticationTokens, issuedByApi: boolean) => {
      // a tab signed in again as someone else while the refresh was out keeps that session
      if (!authStore.tokens || !isSameWebLogin(authStore.tokens.jsonWebToken, refreshedTokens.jsonWebToken)) {
        return undefined;
      }

      // the kept session was signed out, or taken by another login, in another tab while the refresh was out;
      // storing it would undo that
      if (sharesSession && !isKeptSessionOf(localStorage.getItem("tokens"), refreshedTokens.jsonWebToken)) {
        return undefined;
      }

      // tokens this tab already holds are not stored again: that would read as newly issued
      if (authStore.tokens.jsonWebToken !== refreshedTokens.jsonWebToken) {
        authStore.setWebTokens(refreshedTokens);
      }
      return { tokens: refreshedTokens, issuedByApi };
    };

    // taken up from another tab's refresh while this one waited, as an account switch writing the session back does:
    // refreshing those, unexpired, would be taken for theft and end every kept tab
    if (refused && tokens.jsonWebToken !== refused.jsonWebToken) {
      return useTokensFrom(tokens, false);
    }

    const refreshedInAnotherTab = sharesSession ? getNewerKeptSessionTokens(tokens) : undefined;
    if (refreshedInAnotherTab) {
      return useTokensFrom(refreshedInAnotherTab, false);
    }

    const marksRefresh = sharesSession && !underLock;
    if (marksRefresh) {
      setRefreshMark(true);
    }

    try {
      const response = await axios(buildRefreshRequestConfiguration(tokens));
      const refreshedTokens = response.data as WebLoginAuthenticationTokens | undefined;
      return refreshedTokens ? useTokensFrom(refreshedTokens, true) : undefined;
    } catch (error) {
      if (marksRefresh) {
        setRefreshMark(false);
      }

      // another tab may have replaced the refresh token while this request was on its way; without the lock its
      // refresh may have won the same race as this one and not yet stored the tokens
      const waitForAnotherTab =
        !underLock && isAxiosError(error) && error.response?.status === 401 && isAnotherTabRefreshing();
      const refreshedMeanwhile = sharesSession
        ? await getNewerKeptSessionTokensWithin(tokens, waitForAnotherTab ? UNLOCKED_REFRESH_RACE_WAIT : 0)
        : undefined;
      if (refreshedMeanwhile) {
        return useTokensFrom(refreshedMeanwhile, false);
      }

      throw error;
    } finally {
      if (marksRefresh) {
        setRefreshMark(false);
      }
    }
  };

  if (!navigator.locks) {
    return refresh(false);
  }

  // one tab refreshes at a time, so the next one finds the new tokens rather than refreshing the replaced one
  let lockGranted = false;
  try {
    return await navigator.locks.request("customer-portal-token-refresh", () => {
      lockGranted = true;
      return refresh(true);
    });
  } catch (error) {
    // the browser would not grant the lock (an insecure or sandboxed page): refresh without it. A failure inside the
    // refresh is not retried: the tokens may already be replaced, and refreshing those would be taken for theft
    if (!lockGranted && error instanceof DOMException) {
      return refresh(false);
    }

    throw error;
  }
}

// A request carrying the token in its body as well (changing the password) must be sent again with the new one.
function replaceSentToken(requestConfig: PortalRequestConfig, sentToken: string, newToken: string) {
  requestConfig.headers.Authorization = `Bearer ${newToken}`;

  if (sentToken && typeof requestConfig.data === "string" && requestConfig.data.includes(sentToken)) {
    requestConfig.data = requestConfig.data.split(sentToken).join(newToken);
  }
}

// Usage events are sent here only by the tracker.
function isActivityRequest(url: string | undefined): boolean {
  return Object.values(ACTIVITY_ENDPOINTS).includes(url ?? "");
}

export function initializePortalHttpClient(
  configurationContents: ConfigurationFileContents
) {
  runtimeConfiguration = configurationContents;

  if (import.meta.env.PROD && configurationContents.publicApiBaseUrl) {
    portalHttpClient.defaults.baseURL = configurationContents.publicApiBaseUrl;
  } else {
    delete portalHttpClient.defaults.baseURL;
  }
}

portalHttpClient.interceptors.request.use(
  (requestConfig: InternalAxiosRequestConfig) =>
    applyRuntimeConfiguration(requestConfig) as InternalAxiosRequestConfig
);

/**
 * Exported so the retry rule can be exercised on its own: it decides whether a request is re-sent, and a rule
 * that re-sends on a refusal it cannot change is the difference between one failed read and an endless one.
 */
export async function handleResponseError(error: AxiosError) {
  const authStore = useAuthStore();
  const alertsStore = useAlertsStore();
  const notificationsStore = useNotificationsStore();
  const requestConfig = error.config;
  const activity = isActivityRequest(requestConfig?.url);

  // Usage tracking never shows the grower an alert or records its own failures. Only a 401 goes on, the server's
  // word that the token has expired or the session has ended.
  if (activity && !(error.response?.status === 401 && authStore.tokens)) {
    return Promise.reject(error);
  }

  if (error.response?.status === 422) {
    return error.response;
  }

  if (error.response?.status === 404) {
    trackActivity(
      ActivityCategory.error,
      "404",
      String(router.currentRoute.value.name ?? ""),
      toEndpointTemplate(requestConfig?.url)
    );

    notificationsStore.setNotification({
      message: "The requested resource was not found",
      type: NotificationType.Error,
    } as Notification);

    return Promise.reject(error);
  }

  if (error.response?.status === 403) {
    trackActivity(
      ActivityCategory.error,
      "403",
      String(router.currentRoute.value.name ?? ""),
      toEndpointTemplate(requestConfig?.url)
    );

    if (!(requestConfig as AlertSuppressingRequestConfig)?.suppressErrorAlert) {
      notificationsStore.setNotification({
        message: "You do not have permission to do that",
        type: NotificationType.Error,
      } as Notification);
    }

    return Promise.reject(error);
  }

  if (requestConfig?.url?.includes(REFRESH_TOKEN_ENDPOINT)) {
    await redirectToLoginWithSessionExpiredAlert();
    return Promise.reject(error);
  }

  if (error.response?.status === 401 && authStore.tokens && requestConfig) {
    const portalRequest = requestConfig as PortalRequestConfig;
    const sentAuthorization = String(portalRequest.headers?.Authorization ?? "");
    const sentToken = sentAuthorization.replace(/^Bearer /, "");
    const currentAuthorization = `Bearer ${authStore.tokens.jsonWebToken}`;

    // sent for a login this tab is no longer signed in as: sent again with this login's token it would ask for
    // the other login's account, which ends every session of this login
    if (sentToken && !isSameWebLogin(sentToken, authStore.tokens.jsonWebToken)) {
      return Promise.reject(error);
    }

    // a current token asking for an account the login no longer holds ends every session of the login; a refresh
    // would only be refused. The refusal of a token this tab no longer holds may be from before signing in again,
    // so it ends nothing here: the tab's own next request for that account is refused the same way
    // (Usage events too: by then the server has ended every session of the login, so saying so now is truer than
    // the inactivity message the next renewal would give.)
    if (error.response?.data === ACCOUNT_NOT_HELD_REPLY) {
      if (sentAuthorization === currentAuthorization) {
        await redirectToLoginWithSessionExpiredAlert(SESSION_ENDED_MESSAGE);
      } else if (!activity && notificationsStore.notification?.message !== ACCOUNT_NOT_AVAILABLE_MESSAGE) {
        // once for a page whose several requests for the account are refused alike
        notificationsStore.setNotification({
          message: ACCOUNT_NOT_AVAILABLE_MESSAGE,
          type: NotificationType.Error,
        } as Notification);
      }
      return Promise.reject(error);
    }

    // refused for a token already refreshed since it was sent: refreshing the new one would end the session
    if (!refreshRequest && sentAuthorization !== currentAuthorization) {
      replaceSentToken(portalRequest, sentToken, authStore.tokens.jsonWebToken);
      return portalHttpClient(portalRequest);
    }

    // refused again with a token the API just issued: refusals of what a login may do are 403, so the session has
    // been ended (an account the login no longer holds revokes it); refreshing again would be treated as theft
    if (portalRequest.retriedAfterTokenRefresh) {
      if (!activity) {
        await redirectToLoginWithSessionExpiredAlert(SESSION_ENDED_MESSAGE);
      }
      return Promise.reject(error);
    }

    // a failed refresh for usage events is left for the next real request, so tracking never signs the grower out

    const pendingRefresh = (refreshRequest =
      refreshRequest ??
      refreshAuthenticationTokens().finally(() => {
        refreshRequest = undefined;
      }));

    try {
      const refreshed = await pendingRefresh;

      // signed out, or signed in as someone else, while the refresh was out
      if (!authStore.tokens || (sentToken && !isSameWebLogin(sentToken, authStore.tokens.jsonWebToken))) {
        return Promise.reject(error);
      }

      if (!refreshed) {
        if (activity) {
          return Promise.reject(error);
        }

        // only another login in the shared session shows another tab was at work; an empty one may be the browser's
        // doing, and a sign-out in another tab says so through its own storage event
        const keptSession = localStorage.getItem("tokens");
        const endedInAnotherTab =
          authStore.webLogin?.keepLoggedIn === true &&
          keptSession !== null &&
          !isKeptSessionOf(keptSession, authStore.tokens.jsonWebToken);
        await redirectToLoginWithSessionExpiredAlert(
          endedInAnotherTab ? SESSION_CHANGED_IN_ANOTHER_TAB_MESSAGE : SESSION_EXPIRED_MESSAGE
        );
        return Promise.reject(error);
      }

      // tokens taken from another tab may have expired as well, so a refusal of those still gets a real refresh
      portalRequest.retriedAfterTokenRefresh = refreshed.issuedByApi;
      replaceSentToken(portalRequest, sentToken, refreshed.tokens.jsonWebToken);

      return portalHttpClient(portalRequest);
    } catch (refreshError) {
      // only the API saying the session is over ends it; an outage or a timeout leaves the user signed in
      const sessionOver = isAxiosError(refreshError) && refreshError.response?.status === 401;

      // usage events refused only because the token could not be renewed were never judged, so they are kept
      if (activity && authStore.tokens && !sessionOver) {
        portalRequest.renewalFailed = true;
      }

      // another request waiting on the same refresh has already ended the session
      if (!authStore.tokens || activity) {
        return Promise.reject(error);
      }

      if (sessionOver) {
        await redirectToLoginWithSessionExpiredAlert();
      } else {
        // once for the renewal, however many requests were waiting on it
        if (renewalFailureRecorded !== pendingRefresh) {
          renewalFailureRecorded = pendingRefresh;
          trackActivity(
            ActivityCategory.error,
            String((isAxiosError(refreshError) && refreshError.response?.status) || "Network"),
            String(router.currentRoute.value.name ?? ""),
            toEndpointTemplate(REFRESH_TOKEN_ENDPOINT)
          );
        }

        alertsStore.setAlert({
          headerMessage: "Your session could not be renewed. Please try again.",
          bodyMessage: "",
          type: AlertType.Error,
        } as Alert);
      }

      return Promise.reject(error);
    }
  }

  // a session's request refused after the session ended: the message saying why it ended stays. A request made
  // without a session (signing in) still shows its error
  if (
    error.response?.status === 401 &&
    !authStore.tokens &&
    String(requestConfig?.headers?.Authorization ?? "").startsWith("Bearer ")
  ) {
    return Promise.reject(error);
  }

  trackActivity(
    ActivityCategory.error,
    String(error.response?.status ?? "Network"),
    String(router.currentRoute.value.name ?? ""),
    toEndpointTemplate(requestConfig?.url)
  );

  if (!(requestConfig as AlertSuppressingRequestConfig)?.suppressErrorAlert) {
    alertsStore.setAlert({
      headerMessage: error.message || "Request failed",
      bodyMessage: "",
      type: AlertType.Error,
    } as Alert);
  }

  return Promise.reject(error);
}

portalHttpClient.interceptors.response.use(
  (response) => response,
  handleResponseError
);

function combineUrl(baseUrl: string | undefined, relativeUrl: string) {
  if (!baseUrl) {
    return `/${relativeUrl}`;
  }

  return `${baseUrl.replace(/\/+$/, "")}/${relativeUrl}`;
}

// Sent with fetch rather than axios, so a failure never reaches the interceptor that
// shows alerts, and so a batch flushed as the tab closes can go keepalive and still arrive.
export async function sendPortalActivityBatch(
  batch: PortalActivityBatch,
  keepalive: boolean
) {
  const requestConfig = applyRuntimeConfiguration({});
  const headers = requestConfig.headers as Record<string, string>;
  const query = new URLSearchParams(requestConfig.params as Record<string, string>);

  headers["Content-Type"] = "application/json";
  headers["Portal-Session"] = batch.sessionId;
  headers["Portal-Device"] = batch.device;
  headers["Customer-Id"] = sanitizeHeaderValue(batch.customerId);
  headers["Entity-Id"] = sanitizeHeaderValue(batch.entityId);

  if (batch.channel === "anonymous") {
    delete headers.Authorization;
  }

  const url = combineUrl(
    portalHttpClient.defaults.baseURL,
    ACTIVITY_ENDPOINTS[batch.channel]
  );

  // A stalled connection would otherwise hold back every later routine flush. Safari and
  // iOS before 16 have no AbortSignal.timeout, so they get a timer of their own.
  const controller =
    typeof AbortSignal.timeout === "function" ? undefined : new AbortController();
  const timer = controller
    ? setTimeout(
        () => controller.abort(new DOMException("signal timed out", "TimeoutError")),
        ACTIVITY_SEND_TIMEOUT
      )
    : undefined;

  try {
    const response = await fetch(query.toString() ? `${url}?${query.toString()}` : url, {
      method: "POST",
      headers,
      body: JSON.stringify(batch.events),
      keepalive,
      signal: controller?.signal ?? AbortSignal.timeout(ACTIVITY_SEND_TIMEOUT),
    });

    // The account check ended every session of the login for the token in use: sign out now, as a page request
    // would, rather than leave the grower on a session the next renewal will refuse as inactive.
    if (response.status === 401 && headers.Authorization) {
      const sentAuthorization = headers.Authorization;
      const reply = await response.text().catch(() => "");
      const tokens = useAuthStore().tokens;

      if (reply === ACCOUNT_NOT_HELD_REPLY && tokens && sentAuthorization === `Bearer ${tokens.jsonWebToken}`) {
        await redirectToLoginWithSessionExpiredAlert(SESSION_ENDED_MESSAGE);
        throw new PortalActivityRefusedError(response.status, "sessionEnded");
      }
    }

    // A refusal, the sign in page's throttle among them, says nothing was stored. A
    // server error might have stored it, as might a send that timed out.
    if (response.status >= 400 && response.status < 500) {
      throw new PortalActivityRefusedError(response.status);
    }

    if (!response.ok) {
      throw new Error(`Portal activity failed: ${response.status}`);
    }
  } finally {
    clearTimeout(timer);
  }
}

// For signed in events held because this device's clock says the token has expired. Sent
// through portalHttpClient rather than fetch, so only a 401, the server's own word that
// the token has expired, refreshes it, through the one refresh every request shares, and
// the batch goes again with the new token. A token the server still takes is used as is.
export async function sendHeldPortalActivityBatch(batch: PortalActivityBatch) {
  let status: number;
  let renewalFailed = false;

  try {
    const response = await portalHttpClient.post(ACTIVITY_ENDPOINTS[batch.channel], batch.events, {
      headers: {
        "Customer-Id": sanitizeHeaderValue(batch.customerId),
        "Entity-Id": sanitizeHeaderValue(batch.entityId),
        "Portal-Session": batch.sessionId,
        "Portal-Device": batch.device,
      },
      timeout: ACTIVITY_SEND_TIMEOUT,
    });

    status = response.status;
  } catch (error) {
    status = (error as AxiosError).response?.status ?? 0;
    renewalFailed = ((error as AxiosError).config as PortalRequestConfig | undefined)?.renewalFailed === true;

    if (status < 400 || status >= 500) {
      throw error;
    }
  }

  if (status >= 400 && status < 500) {
    throw new PortalActivityRefusedError(status, renewalFailed ? "renewalFailed" : undefined);
  }
}
